import React, { useEffect, useState } from "react";
import {
  Typography, Box, Tabs, Tab, Button, Divider, TextField, IconButton, Stack, Alert,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";
import AuthApiCall from "../../Apicall/AuthApiCall";
import { useUser } from "../../context/UserContext";
import { postLoginRedirect } from "../../utils/postLoginRedirect";
import BankIdAuthModal from "./BankIdAuthModal";
import { useBankIdReturn } from "../../hooks/useBankIdReturn";
import { findPending, claimResume } from "../../utils/bankIdSession";
import "./Home.css";
import {
  DS,
  modalTitleSx,
  modalLedeSx,
  inkButtonSx,
  outlineButtonSx,
  textButtonSx,
  fieldSx,
  fieldLabelSx,
  tabStripSx,
  tabSx,
  legalSx,
  legalLinkStyle,
} from "./dsModal.js";

// ── Main component ────────────────────────────────────────────────────────────
const LoginModal = (props) => {
  // Palette and control measures come from the shared DS dialog theme; this
  // file no longer carries a colour of its own. See dsModal.js.
  const SURFACE = DS.sheet;
  const TEXT = DS.ink;
  const MUTED = DS.muted;

  // The cross-device QR path is primary on desktop, the same-device path on a
  // phone, exactly as the reference dialog orders them. Matched once here so
  // both the DOM order and the fills follow the same decision.
  const isCompact =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(max-width: 640px)").matches;

  const [isSignUp, setIsSignUp] = useState(false);
  const [innerTab, setInnerTab] = useState(0);   // 0 = BankID | 1 = Email
  const [bankIdHelpOpen, setBankIdHelpOpen] = useState(false);
  const [bankIdInitialTab, setBankIdInitialTab] = useState(null);
  const [bankIdModalOpen, setBankIdModalOpen] = useState(false);
  const [bankIdResumeRef, setBankIdResumeRef] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [apiError2, setApiError2] = useState("");

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { refreshUserInfo } = useUser();

  // Reopen the BankID modal to resume this browser's order after a reload or the BankID app's return.
  useBankIdReturn(() => {
    const pending = findPending({ purpose: "auth", scope: "legacy-login" });
    if (pending && claimResume(pending.orderRef)) {
      setBankIdResumeRef(pending.orderRef);
      setBankIdModalOpen(true);
    }
  });

  const openModalInMode = (signUpMode) => {
    setIsSignUp(signUpMode);
    setInnerTab(0);
    setBankIdHelpOpen(false);
    setApiError("");
    setApiError2("");
  };

  const cleanupBootstrapModalArtifacts = () => {
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("padding-right");
    document.body.style.removeProperty("overflow");
    document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove());
  };

  const setLoginModalDormantState = (isDormant) => {
    const el = document.getElementById("LoginModalModal");
    if (!el) {
      return;
    }

    if (isDormant) {
      el.setAttribute("inert", "");
      el.setAttribute("aria-hidden", "true");
      el.style.pointerEvents = "none";
      el.style.visibility = "hidden";
    } else {
      el.removeAttribute("inert");
      el.removeAttribute("aria-hidden");
      el.style.removeProperty("pointer-events");
      el.style.removeProperty("visibility");
    }
  };

  useEffect(() => {
    return () => {
      cleanupBootstrapModalArtifacts();
      setLoginModalDormantState(false);
    };
  }, []);

  const openBankIdModal = (preferredTab = null) => {
    setBankIdInitialTab(preferredTab);
    const openPortal = () => {
      cleanupBootstrapModalArtifacts();
      setLoginModalDormantState(true);
      setBankIdModalOpen(true);
    };

    try {
      const el = document.getElementById("LoginModalModal");
      const bsModal = window.bootstrap?.Modal?.getInstance(el);

      if (!el || !bsModal || !el.classList.contains("show")) {
        openPortal();
        return;
      }

      const handleHidden = () => {
        el.removeEventListener("hidden.bs.modal", handleHidden);
        try {
          bsModal.dispose();
        } catch (_) {}
        openPortal();
      };

      el.addEventListener("hidden.bs.modal", handleHidden, { once: true });
      bsModal.hide();
    } catch (_) {
      openPortal();
    }
  };

  // ── Store token and redirect ───────────────────────────────────────────────
  const storeTokenAndRedirect = (accessToken) => {
    const encrypted = CryptoJS.AES.encrypt(accessToken, "klargo-secret-key").toString();
    localStorage.setItem("authToken", encrypted);
    cleanupBootstrapModalArtifacts();
    setLoginModalDormantState(false);
    if (props.close?.current) props.close.current.click();
    refreshUserInfo().then(() => postLoginRedirect(navigate));
  };

  // ── Network status ─────────────────────────────────────────────────────────
  useEffect(() => {
    const onOnline = () => setApiError("");
    const onOffline = () => setApiError("No internet connection. Please check your network.");
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    if (!navigator.onLine) setApiError("No internet connection. Please check your network.");
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  // ── Sign Up formik ─────────────────────────────────────────────────────────
  const signupFormik = useFormik({
    initialValues: { email: "", password: "", Confirmpassword: "" },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required("Email is required"),
      password: Yup.string().min(6, "Password must be at least 6 characters").required("Password is required"),
      Confirmpassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Confirm Password is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const email = values.email;
      const password = values.password;
      const payload = {
        userName: email, emailAddress: email,
        personalContact: "", drivingLiscencePath: "", bankDetail: "",
        password: password, profileImagePath: "",
        isSeller: false, isBuyer: false, bankIdSSN: "",
        surname: email, name: email, country: "SE",
        createExternalUserRegisterLogDto: {
          identityToken: "string", aud: "string", authenticationInstant: "string",
          authenticationMethod: "string", authenticationType: "string", countary: "string",
          exp: "1", familyName: "string", given_Name: "string", givenName: "string",
          iat: "1", identityScheme: "string", ipAdress: "string", iss: "string",
          name: "string", nameIdentifier: "string", nbf: "1", sessionIndex: "string",
          ssn: "string", sub: "string", surName: "string",
          userEmail: email, userId: 0,
        },
      };
      try {
        setLoading(true);
        const response = await AuthApiCall({ url: "/services/app/User/RegisterExternalUser", method: "POST", data: payload });
        if (response.data?.success) {
          resetForm();
          // Auto sign-in the just-registered user and send them straight to
          // "Complete your profile" (profile is incomplete: no phone yet).
          try {
            const authRes = await AuthApiCall({
              url: "/TokenAuth/Authenticate",
              method: "POST",
              data: { userNameOrEmailAddress: email, password },
            });
            const token = authRes.data?.result?.accessToken;
            if (token) {
              storeTokenAndRedirect(token);
              return;
            }
          } catch (_) {
            /* auto-login failed — fall back to manual login below */
          }
          Swal.fire({ icon: "success", title: t("auth.signUpSuccess"), text: t("auth.signUpSuccessText") });
          if (props.close?.current) props.close.current.click();
        } else {
          setApiError2(response.data?.error?.details || response.data?.error?.message || "Something went wrong.");
        }
      } catch (error) {
        setApiError2(error.response?.data?.error?.details || error.response?.data?.error?.message || "Something went wrong.");
      } finally {
        setLoading(false);
      }
    },
  });

  // ── Login formik ───────────────────────────────────────────────────────────
  const loginFormik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string().required("Email or Username is required"),
      password: Yup.string().required("Password is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setApiError("");
      try {
        setLoading(true);
        const response = await AuthApiCall({
          url: "/TokenAuth/Authenticate", method: "POST",
          data: { userNameOrEmailAddress: values.email, password: values.password },
        });
        if (response.data?.result?.accessToken) {
          const token = response.data.result.accessToken;
          const encrypted = CryptoJS.AES.encrypt(token, "klargo-secret-key").toString();
          localStorage.setItem("authToken", encrypted);
          resetForm();
          if (props.close?.current) props.close.current.click();
          await refreshUserInfo();
          await postLoginRedirect(navigate);
        } else {
          setApiError(response.data?.error?.details || response.data?.message || "Invalid credentials.");
        }
      } catch (error) {
        setApiError(error.response?.data?.error?.details || error.response?.data?.message || "Something went wrong.");
      } finally {
        setLoading(false);
      }
    },
  });

  const textFieldSx = fieldSx;
  const primaryButtonSx = inkButtonSx;
  const secondaryButtonSx = outlineButtonSx;

  // ── BankID panel ───────────────────────────────────────────────────────────
  const renderBankIdPanel = () => {
    if (bankIdHelpOpen) {
      return (
        <Box sx={{ mt: 1 }}>
          <Button
            onClick={() => setBankIdHelpOpen(false)}
            startIcon={<ArrowBackRoundedIcon />}
            className="klargo-modal-text"
            sx={{ ...textButtonSx, mb: 2, px: 0 }}
          >
            {t("auth.bankId.needHelp")}
          </Button>
          <Stack spacing={2.25}>
            <Box>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: TEXT, mb: 0.5 }}>
                {t("auth.bankId.openBankId")}
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", color: MUTED, lineHeight: 1.7 }}>
                {t("auth.bankId.openAppHint")}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: TEXT, mb: 0.5 }}>
                {t("auth.bankId.scanQr")}
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", color: MUTED, lineHeight: 1.7 }}>
                {t("auth.bankId.scanQrHint")}
              </Typography>
            </Box>
          </Stack>
        </Box>
      );
    }

    /** The same-device path. `openBankIdModal(1)` selects that tab in the
     *  full-screen BankID modal — unchanged. */
    const sameDeviceAction = (variant) => (
      <Button
        key="same-device"
        fullWidth
        variant={variant === "primary" ? "contained" : "outlined"}
        className={variant === "primary" ? "klargo-modal-ink" : "klargo-modal-outline"}
        sx={variant === "primary" ? primaryButtonSx : secondaryButtonSx}
        onClick={() => openBankIdModal(1)}
      >
        {isSignUp ? t("auth.bankId.registerWithBankId") : t("auth.bankId.openBankId")}
      </Button>
    );

    /** The cross-device path — `openBankIdModal(0)` selects the QR tab. */
    const qrAction = (variant) => (
      <Button
        key="qr"
        fullWidth
        variant={variant === "primary" ? "contained" : "outlined"}
        className={variant === "primary" ? "klargo-modal-ink" : "klargo-modal-outline"}
        startIcon={<QrCode2OutlinedIcon />}
        sx={variant === "primary" ? primaryButtonSx : secondaryButtonSx}
        onClick={() => openBankIdModal(0)}
      >
        {t("auth.bankId.scanQr")}
      </Button>
    );

    return (
        <Box sx={{ mt: 1 }}>
          {/* Compact trust line — subordinate to the actions below it. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: MUTED, mb: 2.5 }}>
            <LockOutlinedIcon sx={{ fontSize: 17 }} />
            <Typography sx={{ fontFamily: DS.sans, fontSize: "13px", color: MUTED }}>
              {t("auth.bankId.secureLogin")}
            </Typography>
          </Box>

          {/* Two BankID methods, one filled. The divider sits BETWEEN them,
              because that is what it separates — it used to sit below both,
              where it read as a break before the help link. The cross-device
              QR path leads on desktop and the same-device path on a phone;
              the order is DOM order, so focus follows the eye. */}
          <Stack spacing={1.5}>
            {isCompact ? sameDeviceAction("primary") : qrAction("primary")}

            <Box sx={{ position: "relative", py: 0.5 }} aria-hidden="true">
              <Divider sx={{ borderColor: DS.ruleRow }} />
              <Typography
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  px: 1.25,
                  backgroundColor: SURFACE,
                  color: DS.muted,
                  fontFamily: DS.sans,
                  fontSize: "12px",
                }}
              >
                {t("auth.or")}
              </Typography>
            </Box>

            {isCompact ? qrAction("secondary") : sameDeviceAction("secondary")}
          </Stack>

          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Button
              variant="text"
              startIcon={<HelpOutlineOutlinedIcon />}
              onClick={() => setBankIdHelpOpen(true)}
              className="klargo-modal-text"
              sx={textButtonSx}
            >
              {t("auth.bankId.needHelp")}
            </Button>
          </Box>
        </Box>
      );
  };

  // ── Email login form ───────────────────────────────────────────────────────
  const labelSx = fieldLabelSx;

  const renderLoginForm = () => (
    <form onSubmit={loginFormik.handleSubmit}>
      <Stack spacing={2}>
        <Box>
          <Typography sx={labelSx}>{t("auth.email")}</Typography>
          <TextField
            fullWidth
            type="text"
            name="email"
            value={loginFormik.values.email}
            onChange={loginFormik.handleChange}
            onBlur={loginFormik.handleBlur}
            placeholder={t("auth.emailPlaceholder")}
            sx={textFieldSx}
          />
          {loginFormik.touched.email && loginFormik.errors.email && <Typography color="error" variant="caption">{loginFormik.errors.email}</Typography>}
        </Box>

        <Box>
          <Typography sx={labelSx}>{t("auth.password")}</Typography>
          <TextField
            fullWidth
            type="password"
            name="password"
            value={loginFormik.values.password}
            onChange={loginFormik.handleChange}
            onBlur={loginFormik.handleBlur}
            placeholder={t("auth.passwordPlaceholder")}
            sx={textFieldSx}
          />
          {loginFormik.touched.password && loginFormik.errors.password && <Typography color="error" variant="caption">{loginFormik.errors.password}</Typography>}
        </Box>

        {apiError && <Alert severity="error" sx={{ borderRadius: "12px" }}>{apiError}</Alert>}

        <Button fullWidth variant="contained" type="submit" disabled={loading} className="klargo-modal-ink" sx={primaryButtonSx}>
          {loading ? <CircularProgress size={20} color="inherit" /> : t("auth.login")}
        </Button>

        <Typography textAlign="center" fontSize="0.82rem" color={MUTED} fontWeight={500} sx={{ cursor: "pointer", "&:hover": { color: TEXT } }}>
          {t("auth.forgotPassword")}
        </Typography>
      </Stack>
    </form>
  );

  // ── Email sign-up form ─────────────────────────────────────────────────────
  const renderSignupForm = () => (
    <form onSubmit={signupFormik.handleSubmit}>
      <Stack spacing={2}>
        <Box>
          <Typography sx={labelSx}>{t("auth.email")}</Typography>
          <TextField
            fullWidth
            type="text"
            name="email"
            value={signupFormik.values.email}
            onChange={signupFormik.handleChange}
            onBlur={signupFormik.handleBlur}
            placeholder={t("auth.emailPlaceholder")}
            sx={textFieldSx}
          />
          {signupFormik.touched.email && signupFormik.errors.email && <Typography color="error" variant="caption">{signupFormik.errors.email}</Typography>}
        </Box>

        <Box>
          <Typography sx={labelSx}>{t("auth.password")}</Typography>
          <TextField
            fullWidth
            type="password"
            name="password"
            value={signupFormik.values.password}
            onChange={signupFormik.handleChange}
            onBlur={signupFormik.handleBlur}
            placeholder={t("auth.passwordPlaceholder")}
            sx={textFieldSx}
          />
          {signupFormik.touched.password && signupFormik.errors.password && <Typography color="error" variant="caption">{signupFormik.errors.password}</Typography>}
        </Box>

        <Box>
          <Typography sx={labelSx}>{t("auth.confirmPassword")}</Typography>
          <TextField
            fullWidth
            type="password"
            name="Confirmpassword"
            value={signupFormik.values.Confirmpassword}
            onChange={signupFormik.handleChange}
            onBlur={signupFormik.handleBlur}
            placeholder={t("auth.confirmPasswordPlaceholder")}
            sx={textFieldSx}
          />
          {signupFormik.touched.Confirmpassword && signupFormik.errors.Confirmpassword && <Typography color="error" variant="caption">{signupFormik.errors.Confirmpassword}</Typography>}
        </Box>

        {apiError2 && <Alert severity="error" sx={{ borderRadius: "12px" }}>{apiError2}</Alert>}

        <Button fullWidth variant="contained" type="submit" disabled={loading} className="klargo-modal-ink" sx={primaryButtonSx}>
          {loading ? <CircularProgress size={20} color="inherit" /> : t("auth.createAccount")}
        </Button>
      </Stack>
    </form>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <button
        type="button"
        className="btn btn-primary d-none"
        data-bs-toggle="modal"
        data-bs-target="#LoginModalModal"
        ref={props.open}
        onClick={() => openModalInMode(false)}
      >
        Login
      </button>
      <button
        type="button"
        className="btn btn-primary d-none"
        data-bs-toggle="modal"
        data-bs-target="#LoginModalModal"
        ref={props.openSignUp}
        onClick={() => openModalInMode(true)}
      >
        Sign up
      </button>

      <div className="modal fade" id="LoginModalModal" tabIndex="-1" aria-labelledby="LoginModalModalLabel" data-bs-backdrop="static" aria-hidden="true">
        <div className="modal-dialog modal-md modal-dialog-centered">
          {/* One of the only two places the public surface spends a shadow.
              13px radius with a hairline edge: this is a sheet, and edges in
              this system are drawn, not floated. 24px + a 60px shadow and no
              border was the pre-port card. */}
          <div
            className="modal-content klargo-modal"
            style={{
              borderRadius: "13px",
              overflow: "hidden",
              border: `1px solid ${DS.ruleEdge}`,
              boxShadow: "0 8px 32px rgba(16, 30, 40, 0.16)",
              backgroundColor: DS.sheet,
            }}
          >
            <div className="modal-body" style={{ padding: 0 }}>
              <Box sx={{ p: { xs: 3, sm: 4 }, backgroundColor: SURFACE }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
                  <IconButton
                    type="button"
                    data-bs-dismiss="modal"
                    ref={props.close}
                    aria-label="Close"
                    size="small"
                    sx={{ color: MUTED }}
                  >
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Serif ink title over a sans lede — the dialog is the bridge
                    between the public page and the authenticated product, and
                    it opens in the same voice both of them use. */}
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Typography id="LoginModalModalLabel" sx={{ ...modalTitleSx, mb: 1 }}>
                    {isSignUp ? t("auth.signUpTitle") : t("auth.signInTitle")}
                  </Typography>
                  <Typography sx={{ ...modalLedeSx, maxWidth: 360, mx: "auto" }}>
                    {isSignUp ? t("auth.signUpSubtitle") : t("auth.signInSubtitle")}
                  </Typography>
                </Box>

                <Box sx={{ ...tabStripSx, mb: 3 }}>
                  <Tabs value={innerTab} onChange={(_, v) => setInnerTab(v)}
                    variant="fullWidth" TabIndicatorProps={{ style: { display: "none" } }} textColor="inherit"
                    sx={{ minHeight: "unset" }}>
                    <Tab label="BankID" sx={tabSx(innerTab === 0)} />
                    <Tab label={t("auth.tabs.emailPassword")} sx={tabSx(innerTab === 1)} />
                  </Tabs>
                </Box>

                {innerTab === 0 && renderBankIdPanel()}
                {innerTab === 1 && (isSignUp ? renderSignupForm() : renderLoginForm())}

                {/* A quiet contextual switch — a text link, never a third
                    call to action competing with the two above it. */}
                <Typography
                  textAlign="center"
                  mt={2.5}
                  sx={{ fontFamily: DS.sans, fontSize: "13px", color: MUTED }}
                >
                  {isSignUp
                    ? <>{t("auth.alreadyHaveAccount")}?{" "}<span style={legalLinkStyle} onClick={() => setIsSignUp(false)}>{t("auth.login")}</span></>
                    : <>{t("auth.newToKlargo")}?{" "}<span style={legalLinkStyle} onClick={() => { setIsSignUp(true); setInnerTab(1); }}>{t("auth.createAccount")}</span></>
                  }
                </Typography>

                {/* The lowest-priority object in the dialog: one hairline and
                    11px, so it is available without competing. The document
                    names point at the real public routes. */}
                <Typography variant="caption" sx={legalSx}>
                  {t("auth.agreementTextStart")}{" "}
                  <a href="/villkor" style={legalLinkStyle}>{t("auth.termsOfService")}</a>{" "}
                  {t("auth.agreementTextMiddle")}{" "}
                  <a href="/integritet" style={legalLinkStyle}>{t("auth.privacyPolicy")}</a>.
                </Typography>
              </Box>
            </div>
          </div>
        </div>
      </div>

      {/* ── Full-screen BankID modal ── */}
      <BankIdAuthModal
        open={bankIdModalOpen}
        initialTab={bankIdInitialTab}
        resumeOrderRef={bankIdResumeRef}
        scope="legacy-login"
        onClose={() => {
          setBankIdModalOpen(false);
          setBankIdResumeRef(null);
          setBankIdInitialTab(null);
          setLoginModalDormantState(false);
          // Reshow Bootstrap login modal when BankID is cancelled
          try {
            const el = document.getElementById("LoginModalModal");
            let bsModal = window.bootstrap?.Modal?.getInstance(el);
            if (!bsModal) bsModal = new window.bootstrap.Modal(el);
            bsModal?.show();
          } catch (_) {}
        }}
        onSuccess={(token) => {
          setBankIdModalOpen(false);
          setBankIdResumeRef(null);
          setBankIdInitialTab(null);
          setLoginModalDormantState(false);
          storeTokenAndRedirect(token);
        }}
      />
    </div>
  );
};

export default LoginModal;
